import React, { useState } from 'react';
import {
  Box,
  Text,
  IconButton,
  HStack,
  VStack,
  Spinner,
  Button,
} from '@chakra-ui/react';
import { FiDownload, FiRefreshCw, FiCopy } from 'react-icons/fi';
import { MessageAttachment } from '@/features/messages/messages.types';
import { dataProvider } from '@/services/data-provider';
import { useMutation } from '@tanstack/react-query';

interface AudioTranscriptionProps {
  attachment: MessageAttachment;
  transcription: string | null;
}

interface TranscriptionResponse {
  transcription: string;
  language?: string;
  confidence?: number;
}

export const AudioTranscription: React.FC<AudioTranscriptionProps> = ({
  attachment,
  transcription,
}) => {
  const [currentTranscription, setCurrentTranscription] = useState<
    string | null
  >(transcription);

  const [showFullTranscription, setShowFullTranscription] = useState(false);

  // Mutation for fetching transcription
  const transcribeMutation = useMutation({
    mutationFn: async (fileUrl: string): Promise<TranscriptionResponse> => {
      console.log('Requesting transcription for:', fileUrl);
      const response = await dataProvider.create('audio/transcribe', {
        data: { file_url: fileUrl },
      });

      if (!response || !response.data) {
        throw new Error('Invalid response from transcription endpoint');
      }

      return response.data;
    },
    onSuccess: (data) => {
      setCurrentTranscription(data.transcription);
    },
    onError: (error) => {
      console.error('Transcription error:', error);
    },
  });

  // Don't auto-fetch transcription, let user trigger it

  const handleRetryTranscription = () => {
    setCurrentTranscription(null);
    transcribeMutation.mutate(attachment.file_url);
  };

  const handleDownloadTranscription = () => {
    if (!currentTranscription) return;

    const blob = new Blob([currentTranscription], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${attachment.file_name?.replace(/\.[^/.]+$/, '') || 'audio'}-transcription.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyToClipboard = async () => {
    if (!transcription) return;

    try {
      await navigator.clipboard.writeText(transcription);
      // Could add a toast notification here in the future
      console.log('Transcription copied to clipboard');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = transcription;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const handleCreateTranscription = () => {
    transcribeMutation.mutate(attachment.file_url);
  };

  return (
    <Box width="100%">
      {/* Loading State */}
      {transcribeMutation.isPending && (
        <HStack gap={2} align="center">
          <Spinner size="sm" color="gray.500" />
          <Text fontSize="sm" color="gray.600">
            Transcribing audio...
          </Text>
        </HStack>
      )}

      {/* Error State */}
      {transcribeMutation.error && (
        <VStack gap={2} align="start">
          <Text fontSize="sm" color="red.600">
            Transcription failed:{' '}
            {transcribeMutation.error.message ||
              'Unable to transcribe audio file'}
          </Text>
          <Button
            size="sm"
            variant="subtle"
            colorPalette="gray"
            onClick={handleRetryTranscription}
            fontSize="sm"
            _hover={{ bg: 'gray.200' }}
          >
            <HStack gap={1}>
              <FiRefreshCw size={14} />
              <Text>Try again</Text>
            </HStack>
          </Button>
        </VStack>
      )}

      {/* No transcription yet - Show "Create transcription" button */}
      {!currentTranscription &&
        !transcribeMutation.isPending &&
        !transcribeMutation.error && (
          <Button
            size="sm"
            variant="subtle"
            colorPalette="gray"
            onClick={handleCreateTranscription}
            fontSize="sm"
            _hover={{ bg: 'gray.200' }}
          >
            Create transcription
          </Button>
        )}

      {/* Transcription Preview - Slack Style */}
      {currentTranscription && (
        <VStack align="stretch" gap={1}>
          {/* Transcription Text - Using truncate like Slack */}
          <Text
            fontSize="xs"
            color="gray.700"
            bg="gray.50"
            p={2}
            borderRadius="sm"
            borderLeft="4px solid"
            borderLeftColor="gray.300"
            lineHeight="1.4"
            truncate={!showFullTranscription}
            whiteSpace={showFullTranscription ? 'pre-wrap' : 'nowrap'}
            textAlign="justify"
          >
            {currentTranscription}
          </Text>

          {/* Action Buttons */}
          <HStack justify="space-between" align="center">
            <Button
              size="2xs"
              variant="plain"
              colorPalette="gray"
              onClick={() => setShowFullTranscription(!showFullTranscription)}
              fontSize="xs"
              _hover={{ textDecoration: 'underline' }}
            >
              {showFullTranscription ? 'Show less' : 'View transcription'}
            </Button>

            <HStack gap={2}>
              <IconButton
                aria-label="Copy transcription to clipboard"
                size="xs"
                variant="ghost"
                color="gray.700"
                _hover={{ bg: 'gray.200', color: 'gray.900' }}
                onClick={handleCopyToClipboard}
              >
                <FiCopy size={14} />
              </IconButton>
              <IconButton
                aria-label="Download transcription"
                size="xs"
                variant="ghost"
                color="gray.700"
                _hover={{ bg: 'gray.200', color: 'gray.900' }}
                onClick={handleDownloadTranscription}
              >
                <FiDownload size={14} />
              </IconButton>
            </HStack>
          </HStack>
        </VStack>
      )}
    </Box>
  );
};
